import { FC, memo } from 'react';

import { Button, Select, Space } from '@bringg/react-components';
import { useGetFeatureFlag } from '@bringg-frontend/hooks';
import { OrderHistoryFiltersType } from '@bringg/types';
import { useTranslation } from 'react-i18next';

import { CalculatedCount } from './calculated-count';
import { CountDisplayWithFetch } from './count-display-with-fetch';
import { PaginationProps } from './types';

const FIRST_PAGE = 1;
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export const DISABLE_FETCH_COUNT_FEATURE_FLAG = 'disable_fetch_count_history_page';

interface CountDisplayProps {
	limit: number;
	page: number;
	isFirstPage: boolean;
	nextPage: boolean;
	tasksCount: number;
	filters: OrderHistoryFiltersType;
	countLoadFailedText: string;
	countOutOfText: string;
}

const CountDisplay: FC<CountDisplayProps> = memo(
	({ limit, page, isFirstPage, nextPage, tasksCount, filters, countLoadFailedText, countOutOfText }) => {
		const disableFetchCount = useGetFeatureFlag(DISABLE_FETCH_COUNT_FEATURE_FLAG);

		return disableFetchCount ? (
			<CalculatedCount
				limit={limit}
				page={page}
				nextPage={nextPage}
				tasksCount={tasksCount}
				outOfText={countOutOfText}
			/>
		) : (
			<CountDisplayWithFetch
				limit={limit}
				page={page}
				isFirstPage={isFirstPage}
				filters={filters}
				countLoadFailedText={countLoadFailedText}
				countOutOfText={countOutOfText}
			/>
		);
	}
);

CountDisplay.displayName = 'CountDisplay';

export const Pagination = memo(
	({ limit, page, filters, loading, nextPage, tasksCount, onChange, onSelect }: PaginationProps) => {
		const { t } = useTranslation();
		const isFirstPage = page === FIRST_PAGE;

		return (
			<Space style={{ display: 'flex', justifyContent: 'start' }}>
				<Select
					value={limit}
					options={[
						{ id: 25, name: t('PAGINATION.ITEMS_PER_PAGE', { size: 25 }) },
						{ id: 50, name: t('PAGINATION.ITEMS_PER_PAGE', { size: 50 }) },
						{ id: 100, name: t('PAGINATION.ITEMS_PER_PAGE', { size: 100 }) }
					]}
					onSelect={onSelect}
				/>
				<div className="bringg-vertical-divider" />
				<CountDisplay
					limit={limit}
					page={page}
					isFirstPage={isFirstPage}
					filters={filters}
					nextPage={nextPage}
					tasksCount={tasksCount}
					countLoadFailedText={t('HISTORY_PAGE.COUNT_LOAD_FAILED')}
					countOutOfText={t('HISTORY_PAGE.COUNT_OUT_OF')}
				/>
				<div className="bringg-vertical-divider" />
				<Button
					disabled={loading || isFirstPage}
					onClick={() => {
						if (page > 1) onChange((p: number) => p - 1);
					}}
				>
					{t('PAGINATION.PREVIOUS')}
				</Button>
				<Button disabled={loading || !nextPage} onClick={() => onChange((p: number) => p + 1)}>
					{t('PAGINATION.NEXT')}
				</Button>
			</Space>
		);
	}
);

Pagination.displayName = 'Pagination';

export default Pagination;
